import client from '@/graphql/client'
import { startCodeGeneration } from '@/api/codegenApi'
import GetBoundedContext from '@/graphql/queries/GetBoundedContext'
import AbortCodeGeneration from '@/graphql/mutations/AbortCodeGeneration'
import OnBoundedContextStatusChanged from '@/graphql/subscriptions/OnBoundedContextStatusChanged'
import subscriptionClient from '@/graphql/subscriptionClient'

export default {
  data () {
    return {
      codegenSubscriptions: {}
    }
  },
  mounted () {
    this.checkAndRestoreGenerationState()
  },
  beforeUnmount () {
    Object.values(this.codegenSubscriptions).forEach((sub) => {
      if (sub) {
        try {
          sub.unsubscribe()
        } catch (error) {
          console.error('Error unsubscribing from codegen subscription:', error)
        }
      }
    })
    this.codegenSubscriptions = {}
  },
  methods: {
    async checkAndRestoreGenerationState () {
      if (!this.boundedContext || !this.workflow) {
        return
      }

      const boundedContextId = this.boundedContext.id
      const workflowId = this.workflow.id

      const isGeneratingInStore = this.$store.getters['codegen/isGenerating'](boundedContextId)

      if (isGeneratingInStore) {
        this.isLoading = true
        this.isGenerating = true
        this.loadingText = 'Generating code...'

        this.reconnectSubscription(boundedContextId, workflowId)
        return
      }

      try {
        const result = await client.query({
          operationName: 'getBoundedContext',
          query: GetBoundedContext,
          variables: {
            workflowId,
            id: boundedContextId
          }
        })

        const bcStatus = result?.getBoundedContext

        if (bcStatus && bcStatus.codeGenerationStatus === 'generating') {
          this.isLoading = true
          this.isGenerating = true
          this.loadingText = 'Generating code...'

          this.$store.commit('codegen/setGenerating', {
            boundedContextId,
            name: this.boundedContext.name,
            workflowId
          })

          this.reconnectSubscription(boundedContextId, workflowId)
        }
      } catch (error) {
        console.error('Failed to check generation state:', error)
      }
    },
    reconnectSubscription (boundedContextId, workflowId) {
      if (this.codegenSubscriptions[boundedContextId]) {
        return
      }

      const boundedContextName = this.boundedContext?.name || 'Unknown'

      this.codegenSubscriptions[boundedContextId] = subscriptionClient.subscribe({
        query: OnBoundedContextStatusChanged,
        variables: {
          workflowId,
          boundedContextId
        },
        onNext: async (data) => {
          const status = data.onBoundedContextStatusChanged

          if (status.codeGenerationStatus === 'generating' && status.generationProgress) {
            if (this.boundedContext && this.boundedContext.id === boundedContextId) {
              this.loadingText = status.generationProgress
            }
          } else if (status.codeGenerationStatus === 'completed') {
            if (this.codegenSubscriptions[boundedContextId]) {
              this.codegenSubscriptions[boundedContextId].unsubscribe()
              delete this.codegenSubscriptions[boundedContextId]
            }

            this.$store.commit('codegen/setCompleted', { boundedContextId })

            if (this.boundedContext && this.boundedContext.id === boundedContextId) {
              await this.refreshFilesFromBackend()
            }

            const hasFailures = status.failedItems && status.failedItems.length > 0

            this.$store.commit('snackbar/showMessage', {
              content: hasFailures
                ? `Code generation completed with ${status.failedItems.length} failed item(s) for "${boundedContextName}"`
                : `Code generation completed for "${boundedContextName}"`,
              timeout: 4000,
              color: hasFailures ? 'warning' : 'success'
            }, { root: true })

            if (this.boundedContext && this.boundedContext.id === boundedContextId) {
              this.isLoading = false
              this.isGenerating = false

              if (this.checkSyncWithRepo) {
                await this.checkSyncWithRepo()
              }

              if (hasFailures) {
                const sortedFailedItems = [...status.failedItems].sort((a, b) => a.type.localeCompare(b.type))

                this.$store.dispatch('workflow/clearFailedGenerations')
                for (const failedItem of sortedFailedItems) {
                  this.$store.dispatch('workflow/addFailedGeneration', {
                    type: failedItem.type,
                    name: failedItem.name,
                    domainIds: failedItem.domainIds || []
                  })
                }
                const domainIdsToRetry = sortedFailedItems.flatMap((item) => item.domainIds || [])
                const includeFrontendRetry = sortedFailedItems.some((item) => item.type === 'frontend')
                this.$store.dispatch('workflow/openRetryDialog', {
                  failedItems: this.failedGenerations,
                  retryCallback: async () => {
                    this.$store.dispatch('workflow/clearFailedGenerations')
                    await this.updateApi(
                      true,
                      includeFrontendRetry,
                      domainIdsToRetry.length > 0 ? domainIdsToRetry : null
                    )
                  }
                })
              }
            }
          } else if (status.codeGenerationStatus === 'failed') {
            if (this.codegenSubscriptions[boundedContextId]) {
              this.codegenSubscriptions[boundedContextId].unsubscribe()
              delete this.codegenSubscriptions[boundedContextId]
            }

            this.$store.commit('codegen/setFailed', {
              boundedContextId,
              error: status.codeGenerationError
            })

            this.$store.commit('snackbar/showMessage', {
              content: `Code generation failed for "${boundedContextName}": ${status.codeGenerationError ||
              'Unknown error'}`,
              timeout: 6000,
              color: 'error'
            }, { root: true })

            if (this.boundedContext && this.boundedContext.id === boundedContextId) {
              this.isLoading = false
              this.isGenerating = false
            }
          } else if (status.codeGenerationStatus === 'aborted') {
            if (this.codegenSubscriptions[boundedContextId]) {
              this.codegenSubscriptions[boundedContextId].unsubscribe()
              delete this.codegenSubscriptions[boundedContextId]
            }

            this.$store.commit('codegen/setCompleted', { boundedContextId })

            if (this.boundedContext && this.boundedContext.id === boundedContextId) {
              await this.refreshFilesFromBackend()
            }

            this.$store.commit('snackbar/showMessage', {
              content: `Code generation stopped for "${boundedContextName}"`,
              timeout: 4000,
              color: 'blue'
            }, { root: true })

            if (this.boundedContext && this.boundedContext.id === boundedContextId) {
              this.isLoading = false
              this.isGenerating = false

              if (this.checkSyncWithRepo) {
                await this.checkSyncWithRepo()
              }
            }
          }
        },
        onError: (error) => {
          console.error(`[${boundedContextId}] Subscription error (reconnected):`, error)

          if (this.codegenSubscriptions[boundedContextId]) {
            this.codegenSubscriptions[boundedContextId].unsubscribe()
            delete this.codegenSubscriptions[boundedContextId]
          }

          if (this.boundedContext && this.boundedContext.id === boundedContextId) {
            this.isLoading = false
            this.isGenerating = false
          }
        }
      })
    },
    async abortCodeGeneration () {
      if (!this.boundedContext || !this.workflow) {
        return
      }

      const boundedContextId = this.boundedContext.id
      const workflowId = this.workflow.id

      if (!this.codegenSubscriptions[boundedContextId]) {
        this.$store.commit('snackbar/showMessage', {
          content: 'No code generation in progress',
          timeout: 3000,
          color: 'info'
        }, { root: true })
        return
      }

      try {
        this.loadingText = 'Stopping code generation...'

        await client.query({
          operationName: 'abortCodeGeneration',
          query: AbortCodeGeneration,
          variables: {
            workflowId,
            boundedContextId
          }
        })
      } catch (error) {
        if (this.codegenSubscriptions[boundedContextId]) {
          this.codegenSubscriptions[boundedContextId].unsubscribe()
          delete this.codegenSubscriptions[boundedContextId]
        }

        this.$store.commit('codegen/setCompleted', { boundedContextId })

        this.isLoading = false
        this.isGenerating = false

        this.$store.commit('snackbar/showMessage', {
          content: `Failed to stop code generation: ${error.message}`,
          timeout: 5000,
          color: 'error'
        }, { root: true })
      }
    },
    async updateApi (includeBackend = true, includeFrontend = false, specificDomainIds = null) {
      const boundedContextId = this.boundedContext.id
      const workflowId = this.workflow.id
      const boundedContextName = this.boundedContext.name

      if (this.codegenSubscriptions[boundedContextId]) {
        this.$store.commit('snackbar/showMessage', {
          content: 'Code generation already in progress for this bounded context',
          timeout: 3000,
          color: 'blue'
        }, { root: true })
        return
      }

      this.isAborted = false
      this.isLoading = true
      this.isGenerating = true
      this.loadingText = 'Generating code...'
      this.$store.dispatch('workflow/clearFailedGenerations')

      try {
        const result = await startCodeGeneration({
          boundedContextId,
          workflowId,
          aiVersion: this.selectedAiModel,
          includeBackend,
          includeFrontend,
          specificDomainIds
        })

        if (!result.success) {
          throw new Error(result.message || 'Failed to start code generation')
        }

        this.$store.commit('codegen/setGenerating', {
          boundedContextId,
          name: boundedContextName,
          workflowId
        })

        this.codegenSubscriptions[boundedContextId] = subscriptionClient.subscribe({
          query: OnBoundedContextStatusChanged,
          variables: {
            workflowId,
            boundedContextId
          },
          onNext: async (data) => {
            const status = data.onBoundedContextStatusChanged

            if (status.codeGenerationStatus === 'generating' && status.generationProgress) {
              if (this.boundedContext && this.boundedContext.id === boundedContextId) {
                this.loadingText = status.generationProgress
              }
            } else if (status.codeGenerationStatus === 'completed') {
              if (this.codegenSubscriptions[boundedContextId]) {
                this.codegenSubscriptions[boundedContextId].unsubscribe()
                delete this.codegenSubscriptions[boundedContextId]
              }

              this.$store.commit('codegen/setCompleted', { boundedContextId })

              if (this.boundedContext && this.boundedContext.id === boundedContextId) {
                await this.refreshFilesFromBackend()
              }

              const hasFailures = status.failedItems && status.failedItems.length > 0

              this.$store.commit('snackbar/showMessage', {
                content: hasFailures
                  ? `Code generation completed with ${status.failedItems.length} failed item(s) for "${boundedContextName}"`
                  : `Code generation completed for "${boundedContextName}"`,
                timeout: 4000,
                color: hasFailures ? 'warning' : 'success'
              }, { root: true })

              if (this.boundedContext && this.boundedContext.id === boundedContextId) {
                this.isLoading = false
                this.isGenerating = false

                if (this.checkSyncWithRepo) {
                  await this.checkSyncWithRepo()
                }

                if (hasFailures) {
                  const sortedFailedItems = [...status.failedItems].sort((a, b) => a.type.localeCompare(b.type))

                  const domainIdsToRetry = sortedFailedItems.flatMap((item) => item.domainIds || [])
                  const includeFrontendRetry = sortedFailedItems.some((item) => item.type === 'frontend')

                  this.$store.dispatch('workflow/clearFailedGenerations')
                  for (const failedItem of sortedFailedItems) {
                    this.$store.dispatch('workflow/addFailedGeneration', {
                      type: failedItem.type,
                      name: failedItem.name,
                      domainIds: failedItem.domainIds || []
                    })
                  }
                  this.$store.dispatch('workflow/openRetryDialog', {
                    failedItems: this.failedGenerations,
                    retryCallback: async () => {
                      this.$store.dispatch('workflow/clearFailedGenerations')
                      await this.updateApi(
                        true,
                        includeFrontendRetry,
                        domainIdsToRetry.length > 0 ? domainIdsToRetry : null
                      )
                    }
                  })
                }
              }
            } else if (status.codeGenerationStatus === 'failed') {
              if (this.codegenSubscriptions[boundedContextId]) {
                this.codegenSubscriptions[boundedContextId].unsubscribe()
                delete this.codegenSubscriptions[boundedContextId]
              }

              this.$store.commit('codegen/setFailed', {
                boundedContextId,
                error: status.codeGenerationError
              })

              this.$store.commit('snackbar/showMessage', {
                content: `Code generation failed for "${boundedContextName}": ${status.codeGenerationError ||
                'Unknown error'}`,
                timeout: 6000,
                color: 'error'
              }, { root: true })

              if (this.boundedContext && this.boundedContext.id === boundedContextId) {
                this.isLoading = false
                this.isGenerating = false
              }
            } else if (status.codeGenerationStatus === 'aborted') {
              if (this.codegenSubscriptions[boundedContextId]) {
                this.codegenSubscriptions[boundedContextId].unsubscribe()
                delete this.codegenSubscriptions[boundedContextId]
              }

              this.$store.commit('codegen/setCompleted', { boundedContextId })

              if (this.boundedContext && this.boundedContext.id === boundedContextId) {
                await this.refreshFilesFromBackend()
              }

              this.$store.commit('snackbar/showMessage', {
                content: `Code generation stopped for "${boundedContextName}"`,
                timeout: 4000,
                color: 'blue'
              }, { root: true })

              if (this.boundedContext && this.boundedContext.id === boundedContextId) {
                this.isLoading = false
                this.isGenerating = false

                if (this.checkSyncWithRepo) {
                  await this.checkSyncWithRepo()
                }
              }
            }
          },
          error: () => {
            if (this.codegenSubscriptions[boundedContextId]) {
              this.codegenSubscriptions[boundedContextId].unsubscribe()
              delete this.codegenSubscriptions[boundedContextId]
            }

            this.$store.commit('snackbar/showMessage', {
              content: `Lost connection for "${boundedContextName}"`,
              timeout: 6000,
              color: 'error'
            }, { root: true })

            if (this.boundedContext && this.boundedContext.id === boundedContextId) {
              this.isLoading = false
              this.isGenerating = false
            }
          }
        })
      } catch (error) {
        this.$store.commit('snackbar/showMessage', {
          content: error.message || 'Code generation failed',
          timeout: 6000,
          color: 'error'
        }, { root: true })

        this.isLoading = false
        this.isGenerating = false

        if (this.codegenSubscriptions[boundedContextId]) {
          this.codegenSubscriptions[boundedContextId].unsubscribe()
          delete this.codegenSubscriptions[boundedContextId]
        }
      }
    },

    async refreshFilesFromBackend () {
      const objects = await this.$store.dispatch('boundedContexts/getObjectsFromServer', {
        boundedContextId: this.boundedContext.id
      })

      this.fileTree = {}
      this.addFiles(objects)
    }
  }
}
